/**
 * Workshop version of ImageFrame.
 *
 * Props-based, no React Flow or store dependencies.
 */

import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import { FrameHeader } from "@/workshop/components/shapes/shared/frame-header";
import { FrameFooter } from "@/workshop/components/shapes/shared/frame-footer";
import { cn } from "@/lib/cn";

interface ImageItem {
  url: string;
  alt?: string;
  caption?: string;
}

function statusColor(status: string): string {
  if (status === "streaming") return "bg-status-streaming";
  if (status === "error") return "bg-status-error";
  return "bg-muted";
}

interface ImageFrameProps {
  title: string;
  images: ImageItem[];
  status: "idle" | "streaming" | "error" | "done";
  agentName?: string;
  onResizeStart?: React.MouseEventHandler;
}

export function ImageFrame({
  title,
  images,
  status,
  agentName,
  onResizeStart,
}: ImageFrameProps) {
  return (
    <div
      className="denker-glass-frame flex flex-col rounded-frame overflow-hidden"
      data-testid="image-frame"
    >
      <FrameHeader
        title={title || "Images"}
        icon={Icons.Image}
        accentColor="bg-frame-image"
        agentName={agentName}
        statusColor={statusColor(status)}
        data-testid="image-frame-header"
      />

      <div className="scrollbar-none flex-1 overflow-auto px-3 py-2">
        {status === "streaming" && images.length === 0 ? (
          <div className="flex items-center gap-2 py-4">
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">Loading images...</span>
          </div>
        ) : images.length > 0 ? (
          <div
            className={cn(
              "grid gap-1.5",
              images.length === 1 && "grid-cols-1",
              images.length >= 2 && "grid-cols-2",
            )}
          >
            {images.map((img, idx) => (
              <div
                key={img.url || idx}
                className="overflow-hidden rounded-[4px] border border-glass-stroke"
                data-testid={`image-item-${idx}`}
              >
                <img
                  src={img.url}
                  alt={img.alt ?? `Image ${idx + 1}`}
                  className="h-auto w-full object-cover"
                  loading="lazy"
                />
                {img.caption && (
                  <p className="bg-elevated/40 px-1.5 py-1 text-canvas-base text-muted">
                    {img.caption}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="py-2 text-center text-canvas-base italic text-muted">
            No images yet
          </p>
        )}
      </div>

      <FrameFooter onResizeStart={onResizeStart} data-testid="image-frame-footer" />
    </div>
  );
}

export type { ImageFrameProps, ImageItem };
